import { useMemo } from "react";
import { useGameStore } from "@/store/gameStore";
import type { GameEvent, PlayerState, Equipment, Team } from "@/types/game";
import type { Hero } from "@/types/heroes";
import type { DeckCard } from "@/types/cards";

interface EquipSlot {
  key: keyof Equipment;
  label: string;
}
const SLOTS: EquipSlot[] = [
  { key: "weapon", label: "武器" },
  { key: "armor", label: "防具" },
  { key: "accessory", label: "饰品" },
];

function PlayerSummary({ player }: { player: PlayerState }) {
  const teamColor = player.team === "A" ? "var(--gold)" : "var(--red)";
  return (
    <div
      className={`game-over-screen__player${
        !player.isAlive ? " game-over-screen__player--dead" : ""
      }`}
      style={{ borderLeftColor: teamColor }}
    >
      <div className="game-over-screen__player-head">
        <span className="game-over-screen__player-name" style={{ color: teamColor }}>
          {player.hero.name}
        </span>
        {!player.isAlive && (
          <span className="game-over-screen__dead-tag">阵亡</span>
        )}
        {player.isAlive && (
          <span className="game-over-screen__alive-tag">存活</span>
        )}
      </div>
      <div className="game-over-screen__stat">
        HP {player.hp}/{player.maxHp}
      </div>
      <div className="game-over-screen__stat">
        MP {player.mp}/{player.maxMp}
      </div>
      <div className="game-over-screen__equip">
        {SLOTS.map((s) => {
          const c = player.equip[s.key];
          return (
            <div
              key={s.key}
              className={`game-over-screen__equip-cell${
                c ? "" : " game-over-screen__equip-cell--empty"
              }`}
              title={c?.name}
            >
              {c ? c.name : s.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function GameOverScreen() {
  const state = useGameStore((s) => s.state);
  const data = useGameStore((s) => s.data);
  const newGame = useGameStore((s) => s.newGame);
  const backToSelect = useGameStore((s) => s.backToSelect);

  const winner = useMemo<Team[]>(() => {
    if (!state) return [];
    const ev = [...state.log].reverse().find((e: GameEvent) => e.type === "game-over");
    if (ev?.type === "game-over") return ev.winners;
    return [];
  }, [state]);

  if (!state) return null;

  const winnerLabel =
    winner.length === 1
      ? `${winner[0]} 队获胜`
      : winner.length > 1
        ? `${winner.join("、")} 队获胜`
        : "平局";

  function restart() {
    if (!data) return;
    const DEFAULT_NAMES = ["法师", "战士", "治愈天使", "巨剑骑士"];
    const heroes = data.heroes;
    const fallback = heroes[0];
    if (!fallback) return;
    const chosen = DEFAULT_NAMES.map((name) => {
      const found = heroes.find((h) => h.name === name);
      return found ?? fallback;
    });
    if (chosen.length !== 4 || chosen.some((h) => !h)) return;
    newGame({
      heroes: chosen as [Hero, Hero, Hero, Hero],
      humanSeat: 0,
      deck: data.deck as DeckCard[],
      seed: Date.now() % 100000,
    });
  }

  return (
    <div className="game-over-screen" role="dialog" aria-modal="true">
      <h1 className="game-over-screen__title">游戏结束</h1>
      <p className="game-over-screen__winner">{winnerLabel}</p>
      <p className="game-over-screen__round">共 {state.round} 圈</p>
      <div className="game-over-screen__players">
        {state.players.map((p) => (
          <PlayerSummary key={p.id} player={p} />
        ))}
      </div>
      <div className="game-over-screen__actions">
        <button
          className="game-over-screen__btn"
          onClick={restart}
          disabled={!data}
        >
          再来一局
        </button>
        <button
          className="game-over-screen__btn game-over-screen__btn--ghost"
          onClick={() => backToSelect()}
        >
          返回选英雄
        </button>
      </div>
    </div>
  );
}
